export const WHATSAPP_TRACKING_EVENTS = {
  // Page and navigation events
  INTEGRATIONS_PAGE_VIEWED: 'whatsapp_integrations_page_viewed',
  SETUP_STEP_VIEWED: 'whatsapp_setup_step_viewed',

  // Account management events
  ACCOUNT_DISCONNECT_CLICKED: 'whatsapp_account_disconnect_clicked',
  ACCOUNT_DISCONNECTED: 'whatsapp_account_disconnected',
  ACCOUNT_LINKED: 'whatsapp_account_linked',
  GROUP_DISCONNECT_CLICKED: 'whatsapp_group_disconnect_clicked',
  GROUP_DISCONNECTED: 'whatsapp_group_disconnected',

  // Permission events
  CAPTION_PERMISSIONS_CHECKBOX_CLICKED: 'whatsapp_caption_permissions_checkbox_clicked',
  CAPTION_PERMISSIONS_GRANTED: 'whatsapp_caption_permissions_granted',

  // Connection events
  BUCKET_CONNECTED: 'whatsapp_bucket_connected',

  // Sidebar events (already implemented)
  SIDEBAR_CONNECT_CLICKED: 'whatsapp_sidebar_connect_clicked'
} as const

export type WhatsAppTrackingEvent = typeof WHATSAPP_TRACKING_EVENTS[keyof typeof WHATSAPP_TRACKING_EVENTS]

// Configuration constants for WhatsApp tracking
export const WHATSAPP_TRACKING_CONFIG = {
  // Step numbers for setup flow
  SETUP_STEPS: {
    PHONE_NUMBER: 1,
    PERMISSIONS: 2,
    QR_CODE: 3,
    CONNECTING: 4,
    BUCKET_SELECTION: 5
  },

  // Performance monitoring thresholds
  PERFORMANCE: {
    SLOW_TRACKING_THRESHOLD: 100, // milliseconds
    MAX_PROPERTY_LENGTH: 1000, // characters
    MAX_PROPERTIES_COUNT: 50
  },

  // Source page identifiers
  SOURCE_PAGES: {
    INTEGRATIONS_MANAGE: 'integrations_manage',
    WHATSAPP_SETUP: 'whatsapp_setup',
    WHATSAPP_PERMISSIONS: 'whatsapp_permissions'
  },

  // Reason codes for tracking events
  REASONS: {
    USER_CHOICE: 'user_choice',
    USER_INITIATED: 'user_initiated',
    DISCONNECT_FAILED: 'disconnect_failed'
  },

  // Disconnection methods
  DISCONNECTION_METHODS: {
    PHOTOBUCKET_UI: 'photobucket_ui',
    WHATSAPP_UNLINK: 'whatsapp_unlink'
  },

  // Integration status values
  INTEGRATION_STATUS: {
    NONE: 'none',
    ACTIVE: 'active',
    PENDING: 'pending',
    FAILED: 'failed'
  }
} as const

// Base properties that should be included in all tracking events
export interface BaseTrackingProperties {
  user_id?: string
  session_id?: string
  timestamp?: string
  current_url?: string
  source_page?: string
  step_number?: number
}

// Specific event property definitions for type safety
export interface IntegrationsPageViewedProperties extends BaseTrackingProperties {
  has_whatsapp_integration: boolean
  integration_status: string
  source_referrer: string
}

export interface AccountDisconnectProperties extends BaseTrackingProperties {
  integration_id: string
  source_page: string
  reason?: string
  disconnection_method?: 'photobucket_ui' | 'whatsapp_unlink'
  detection_method?: 'webhook_failure' | 'api_error' // For WhatsApp-initiated disconnects
}

export interface PermissionEventProperties extends BaseTrackingProperties {
  permission_granted?: boolean
  step_number: number
  user_id: string
  reason?: string
}

export interface GroupManagementProperties extends BaseTrackingProperties {
  integration_mapping_id: string
  integration_id: string
  source_page: string
  disconnection_method?: 'photobucket_ui' | 'whatsapp_unlink'
}

// Backend event interfaces for media upload tracking
export interface MediaUploadCountProperties {
  bucket_id: string
  media_type: 'image' | 'video'
  group_name?: string
  upload_time: string // ISO timestamp
  user_id: string
  file_size?: number
  file_format?: string
}

export interface MediaUploadFrequencyProperties {
  bucket_id: string
  group_name?: string
  user_id: string
  uploads_last_24h: number
  uploads_last_7d: number
  uploads_last_30d: number
  average_daily_uploads: number
}

export interface FirstMediaUploadProperties {
  integration_id: string
  bucket_id: string
  group_name?: string
  user_id: string
  setup_completion_time: string // ISO timestamp
  first_upload_time: string // ISO timestamp
  time_to_first_upload_hours: number
}

export interface IntegrationLifecycleProperties {
  integration_id: string
  bucket_id: string
  group_name?: string
  user_id: string
  first_upload_time: string // ISO timestamp
  last_upload_time: string // ISO timestamp
  usage_duration_days: number
  total_uploads: number
}

// Union type for all possible event properties
export type WhatsAppEventProperties =
  | IntegrationsPageViewedProperties
  | AccountDisconnectProperties
  | PermissionEventProperties
  | GroupManagementProperties
  | MediaUploadCountProperties
  | MediaUploadFrequencyProperties
  | FirstMediaUploadProperties
  | IntegrationLifecycleProperties
  | BaseTrackingProperties
